import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '../api/queryClient';
import type { OAuthProvider, AuthTokens } from '../types/auth.types';
import type { AuthUser } from '../types/user.types';
import { getAuthAdapter } from '../auth/authAdapter';
import { useOrganizationStore } from './organization.store';
import { setSentryContext } from '@/core/observability/public';

/** Status of the bootstrap auth flow (session restore / OAuth callback). */
export type AuthInitStatus = 'initializing' | 'authenticated' | 'error';
/** 'auth' = token rejected (terminal, retry won't help); 'network' = transient. */
export type AuthErrorKind = 'auth' | 'network';

interface AuthState {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  hasHydrated: boolean;
  loading: boolean;
  authStatus: AuthInitStatus;
  authErrorKind: AuthErrorKind | null;

  // actions
  loginWithProvider: (provider: OAuthProvider) => Promise<void>;
  setSession: (user: AuthUser, tokens: AuthTokens) => void;
  updateUser: (updates: Partial<AuthUser>) => void;
  clearSession: () => void;
  logout: () => Promise<void>;
  setTokens: (tokens: AuthTokens) => void;
  setLoading: (loading: boolean) => void;
  failAuthInit: (kind: AuthErrorKind) => void;
  resetAuthInit: () => void;

  // selectors
  isTokenValid: () => boolean;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      hasHydrated: false,
      loading: true,
      authStatus: 'initializing',
      authErrorKind: null,

      loginWithProvider: async (provider) => {
        await getAuthAdapter().loginWithProvider(provider);
      },

      setSession: (user, tokens) => {
        set({ user, tokens, authStatus: 'authenticated', authErrorKind: null });
        setSentryContext(user.id, useOrganizationStore.getState().activeOrganizationId ?? '');
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : state.user,
        }));
      },

      setTokens: (tokens) => {
        set((state) => ({ ...state, tokens }));
      },

      clearSession: () => {
        set({ user: null, tokens: null });
        queryClient.clear();
      },

      failAuthInit: (kind) => {
        set({ authStatus: 'error', authErrorKind: kind });
      },

      resetAuthInit: () => {
        set({ authStatus: 'initializing', authErrorKind: null });
      },

      logout: async () => {
        await getAuthAdapter().logout();
        get().clearSession();
        useOrganizationStore.getState().clear();
      },

      setLoading: (loading: boolean) => {
        set({ loading });
      },

      isTokenValid: () => {
        const tokens = get().tokens;
        if (!tokens) return false;
        return Date.now() < tokens.accessTokenExpiresAt;
      },

      isAuthenticated: () => {
        return !!get().user && get().isTokenValid();
      },
    }),
    {
      name: 'auth-storage',
      // Only the session is persisted; runtime flags (loading, authStatus, errors)
      // must always start fresh so a stale 'error' never survives a reload.
      partialize: (state) => ({ user: state.user, tokens: state.tokens }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hasHydrated = true;
          if (state.user) {
            setSentryContext(
              state.user.id,
              useOrganizationStore.getState().activeOrganizationId ?? '',
            );
          }
        }
      },
    },
  ),
);

// Re-tag Sentry when the active organization changes
useOrganizationStore.subscribe((state, prev) => {
  if (state.activeOrganizationId === prev.activeOrganizationId) return;
  const user = useAuthStore.getState().user;
  if (user) setSentryContext(user.id, state.activeOrganizationId ?? '');
});
